import { generateKeyBetween } from 'fractional-indexing';
import type { BoardDoc } from './BoardDoc';
import type { Item, ItemPatch } from './schema';

/**
 * generateKeyBetween that survives malformed neighbours (a buggy or foreign client could store
 * any string as z). Falls back to ignoring the bad side, then to a fresh key.
 */
export function safeKeyBetween(a: string | null, b: string | null): string {
  try {
    return generateKeyBetween(a, b);
  } catch {
    try {
      return generateKeyBetween(a, null);
    } catch {
      try {
        return generateKeyBetween(null, b);
      } catch {
        return generateKeyBetween(null, null);
      }
    }
  }
}

/** Z-order commands. All operate on the given ids as a block, preserving their relative order. */

export function bringToFront(board: BoardDoc, ids: Set<string>): void {
  const all = board.index.all();
  const moving = all.filter((i) => ids.has(i.id));
  if (!moving.length) return;
  let prev = board.topZ();
  const patches: [string, ItemPatch][] = [];
  for (const item of moving) {
    const z = safeKeyBetween(prev, null);
    patches.push([item.id, { z }]);
    prev = z;
  }
  board.updateItems(patches);
}

export function sendToBack(board: BoardDoc, ids: Set<string>): void {
  const all = board.index.all();
  const moving = all.filter((i) => ids.has(i.id));
  if (!moving.length) return;
  const first = all[0];
  let next: string | null = first ? first.z : null;
  const patches: [string, ItemPatch][] = [];
  // Insert in reverse so relative order is kept.
  for (let i = moving.length - 1; i >= 0; i--) {
    const item = moving[i]!;
    const z = safeKeyBetween(null, next);
    patches.push([item.id, { z }]);
    next = z;
  }
  board.updateItems(patches);
}

export function bringForward(board: BoardDoc, ids: Set<string>): void {
  const all = board.index.all();
  const patches: [string, ItemPatch][] = [];
  // Walk from the top so each moved item jumps over exactly one unselected neighbour.
  for (let i = all.length - 2; i >= 0; i--) {
    const item = all[i]!;
    if (!ids.has(item.id)) continue;
    const above = findNeighbour(all, i, +1, ids);
    if (!above) continue;
    const aboveAbove = all[above.index + 1] ?? null;
    patches.push([item.id, { z: safeKeyBetween(above.item.z, aboveAbove ? aboveAbove.z : null) }]);
  }
  if (patches.length) board.updateItems(patches);
}

export function sendBackward(board: BoardDoc, ids: Set<string>): void {
  const all = board.index.all();
  const patches: [string, ItemPatch][] = [];
  for (let i = 1; i < all.length; i++) {
    const item = all[i]!;
    if (!ids.has(item.id)) continue;
    const below = findNeighbour(all, i, -1, ids);
    if (!below) continue;
    const belowBelow = all[below.index - 1] ?? null;
    patches.push([item.id, { z: safeKeyBetween(belowBelow ? belowBelow.z : null, below.item.z) }]);
  }
  if (patches.length) board.updateItems(patches);
}

function findNeighbour(
  all: Item[],
  from: number,
  dir: 1 | -1,
  skip: Set<string>,
): { item: Item; index: number } | null {
  for (let j = from + dir; j >= 0 && j < all.length; j += dir) {
    const candidate = all[j]!;
    if (!skip.has(candidate.id)) return { item: candidate, index: j };
  }
  return null;
}
